import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { BehaviorSubject } from 'rxjs';
import { AppService } from 'src/app/app.service';
import { SettingsApi } from 'src/app/shared/apis/settings.api';
import { CsConfirmPopupComponent } from 'src/app/shared/components/cs-popup/cs-confirm-popup/cs-confirm-popup.component';
import { CsPopupService } from 'src/app/shared/components/cs-popup/cs-popup.service';
import { AccountInforData } from 'src/app/shared/interfaces/account.interface';
import { CurrenciesInterface } from 'src/app/shared/interfaces/deposit.interface';
import { ISetDefaultCurrency } from 'src/app/shared/interfaces/settings.interface';
import { MatConnectedDialogRef } from 'src/app/shared/service/connected-dialog';
import { ToastService } from 'src/app/shared/service/toast.service';
import { PaymentIqService } from '../../shared/components/select-deposit-bonus/payment-iq/payment-iq.service';
import { SecurityService } from './security/security.service';
@UntilDestroy()
@Injectable({
  providedIn: 'root',
})
export class SettingsService {
  constructor(
    private settingsApi: SettingsApi,
    private toast: ToastService,
    private appService: AppService,
    private piqService: PaymentIqService,
    private csPopupService: CsPopupService,
    private router: Router,
    private readonly securityService: SecurityService,
  ) {
    this.appService.currencies$.pipe(untilDestroyed(this)).subscribe(currencies => {
      this.currencies = currencies;
    });

    this.appService.userInfo$.pipe(untilDestroyed(this)).subscribe(res => {
      this.userInfo = res;
    });
  }

  /** 所有货币啊 */
  currencies!: CurrenciesInterface[];

  /**  设置默认货币订阅 */
  setDefaultCurrencyLoading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  /** 是否开启低佣金 */
  enableCredit$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);

  /** 隐身模式 */
  invisibleMode$: BehaviorSubject<'ShowUserName' | 'ShowUid' | 'Invisibility' | ''> = new BehaviorSubject<
    'ShowUserName' | 'ShowUid' | 'Invisibility' | ''
  >('');

  /** 用戶訊息 */
  userInfo: AccountInforData | null = null;

  /**
   * 设置用户默认币种
   *
   * @param params 默认货币api 参数
   * @param currency 当前选择的默认货币
   */
  setUserDefaultCurrency(params: ISetDefaultCurrency, currency: string): void {
    this.setDefaultCurrencyLoading$.next(true);
    this.piqService.checkedLoading.set(true);
    this.settingsApi.setUserDefaultCurrency(params).subscribe(data => {
      this.setDefaultCurrencyLoading$.next(false);
      this.piqService.checkedLoading.set(false);
      if (data) {
        this.toast.show({ message: 'Default currency setting successful', type: 'success' });
        this.setAppServiceCurrentCurrency(currency);
      } else {
        this.toast.show({ message: 'Connection setup failed', type: 'fail' });
      }
    });
  }

  /**
   * 设置 全局货币与默认货币关联起来
   *
   * @param defaultCurrency 设置默认货币
   */
  setAppServiceCurrentCurrency(defaultCurrency: string) {
    if (!defaultCurrency) return;

    if (this.currencies.find(currency => currency.currency === defaultCurrency)) {
      this.appService.setDefaultWalletCurrency(defaultCurrency);
    }
    //console.log("订阅默认货币了", currency)
  }

  /**
   * 打開啟用2FA的提示彈窗
   *
   * @returns {MatConnectedDialogRef<CsConfirmPopupComponent, any>} dialogRef
   * @memberof SettingsService
   */
  public openEnable2faDialog(): MatConnectedDialogRef<CsConfirmPopupComponent, any> {
    return this.csPopupService.error({
      header: 'Verification required',
      title: 'No Two-Factor Authentication Detected',
      message: 'Please enable a 2FA method in order to perform this action',
      confirmText: 'Enable 2FA',
      color: 'accent',
      confirmCallback: () => {
        this.router.navigate([this.appService.languageCode, 'settings', 'security'], {
          queryParams: {},
          fragment: undefined,
        });
      },
    });
  }

  /**
   * 如果使用者名稱為 5 個字元或更短，則只顯示第一個字元，其餘部分隱藏。
   * 如果使用者名稱超過 5 個字元，則顯示前兩個與最後兩個字元，中間部分隱藏。
   *
   * @param email
   * @returns masked 邮箱
   */
  public getMaskedEmail(email: string): string {
    if (!email.length) return '';
    const atIndex = email.indexOf('@');
    if (atIndex === -1) return '';

    const username = email.substring(0, atIndex);
    let maskedUsername = '';

    if (username.length <= 5) {
      maskedUsername = username[0] + '*'.repeat(username.length - 1);
    } else {
      maskedUsername =
        username.substring(0, 2) + '*'.repeat(username.length - 4) + username.substring(username.length - 2);
    }

    return maskedUsername + email.substring(atIndex);
  }

  /**
   * 檢查是否有任何2FA方法綁定
   *
   * @returns {boolean} 如果有任何2FA方法綁定則返回true，否則返回false
   */
  public hasAny2FA(): boolean {
    return !!(this.userInfo?.isBindEmail || this.userInfo?.isBindMobile || this.userInfo?.isBindGoogleValid);
  }

  public hasSocialEmail(userInfo: AccountInforData | null = this.userInfo): boolean {
    return !!userInfo?.socialEmail?.trim();
  }

  public openConnectedAccountDetectedDialog(): MatConnectedDialogRef<CsConfirmPopupComponent, any> {
    return this.csPopupService.confirm({
      type: 'error',
      header: 'UPDATE EMAIL',
      title: 'Connected Account Detected',
      message: 'To change your email address, you must disconnect your connected account in the security settings',
      cancelText: 'Cancel',
      confirmText: 'Manage Account',
      color: 'primary',
      textAlign: 'center',
      confirmCallback: () => {
        this.router.navigate([this.appService.languageCode, 'settings', 'security']);
      },
    });
  }

  public disconnectionUnavailableDialog() {
    const ref = this.csPopupService.error({
      header: 'Disconnection unavailable',
      title: 'No Alternative Login Method',
      message: 'Please ensure you have a valid email and password before disconnecting this account',
      confirmText: 'Close',
      color: 'accent',
    });
    return ref;
  }

  public isDisconnectionAvailable() {
    const isMultipleThirdPartyConnection =
      Object.values(this.securityService.thirdPartyInfoTable).reduce((acc, cur) => {
        return cur.isBinded ? acc + 1 : acc;
      }, 0) > 1;
    let isEmailLogin = false;
    this.appService.userInfo$.subscribe(res => {
      const { email, hasPassword } = res || {};
      isEmailLogin = !!email && !!hasPassword;
    });
    const available = isEmailLogin || isMultipleThirdPartyConnection;
    return available;
  }
}
